"use client";

import { useEffect } from "react";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import { useBookingStepperStore } from "../states/booking-stepper.store";
import { Step1SessionType } from "../components/stepper/step-1-session-type";
import { Step2ModeDuration } from "../components/stepper/step-2-mode-duration";
import { Step3DateTime } from "../components/stepper/step-3-date-time";
import { Step4ConcernForm } from "../components/stepper/step-4-concern-form";
import { Step5ReviewPay } from "../components/stepper/step-5-review-pay";
import { HoldCountdown } from "../components/stepper/hold-countdown";
import { StepperProgress } from "../components/stepper/shared";
interface BookingStepperPageProps {
  backHref?: string;
  confirmationHrefForBooking?: (bookingId: string) => string;
  healerId: string;
  healerName: string;
  profileHref?: string;
  healerSlug: string;
  locale: AppLocale;
}
export function BookingStepperPage({
  backHref,
  confirmationHrefForBooking,
  healerId,
  healerName,
  profileHref,
  healerSlug,
  locale
}: BookingStepperPageProps) {
  const isHindi = locale === "hi";
  const step = useBookingStepperStore(state => state.step);
  const reset = useBookingStepperStore(state => state.reset);

  // Start every visit to the stepper from a clean slate — avoids stale
  // holds/selections leaking in from a previous booking attempt.
  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <div className="min-h-screen bg-[#FAF8F5] px-4 py-10 font-sans text-slate-900 sm:px-6">
      <div className="mx-auto max-w-2xl">
        <Link href={backHref ?? localizeHref(locale, `/healers/${healerSlug}`)} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-[#155A7D]">
          <ArrowLeft className="h-4 w-4" />
          {isHindi ? `${healerName} की प्रोफ़ाइल पर वापस जाएं` : `Back to ${healerName}'s profile`}
        </Link>

        <div className="mt-6 rounded-3xl border border-[#EAE5DA] bg-white p-6 shadow-[0_12px_30px_rgba(14,42,58,0.06)] sm:p-8">
          <StepperProgress step={step} isHindi={isHindi} />
          <HoldCountdown locale={locale} />

          {step === 1 && <Step1SessionType locale={locale} />}
          {step === 2 && <Step2ModeDuration healerId={healerId} locale={locale} />}
          {step === 3 && <Step3DateTime healerId={healerId} locale={locale} />}
          {step === 4 && <Step4ConcernForm locale={locale} />}
          {step === 5 && <Step5ReviewPay healerName={healerName} healerSlug={healerSlug} changeHealerHref={profileHref} confirmationHrefForBooking={confirmationHrefForBooking} locale={locale} />}
        </div>
      </div>
    </div>;
}